import React, {useEffect, useState} from 'react';
import {Card, CardContent, Box, Link, Typography} from '@mui/material';
import {ChevronDownDark, ChevronUpDark} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {useInView, useIsCustomWidth} from '@jobseeker/lotus/hooks';
import {HighLights, JDQueryParams} from '../type';
import {getKeyFromPath, TrackEventUBA} from '@jobseeker/common-core/utils';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
import dynamic from 'next/dynamic';

const BottomSheet = dynamic(
  () => import('@jobseeker/lotus/elements/BottomSheet'),
  {ssr: false}
);

const CompanyHighLights: React.FC<{
  highLights: HighLights[];
  heading: string;
  isAppIimjobs: boolean;
  templateInfo?: {
    templateNo?: string;
    origin?: string;
  };
  isExpanded?: boolean;
  onToggle?: () => void;
  jobInfo?: any;
  jdQueryParams?: JDQueryParams;
}> = ({
  highLights,
  heading,
  isAppIimjobs,
  templateInfo,
  isExpanded = false,
  onToggle,
  jobInfo,
  jdQueryParams,
}) => {
  const [open, setOpen] = useState(false);

  const showAll = isExpanded;
  const toggleShowAll = onToggle || (() => {});
  const {ref, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const [companyHighlightsTracked, setCompanyHighlightsTracked] =
    useState(false);

  useEffect(() => {
    if (isInView && !companyHighlightsTracked) {
      TrackEventUBA('sectionView', 'view', {
        ref: jdQueryParams?.ref || '',
        pageName: 'JD',
        jobID: jobInfo?.id,
        page: 'JD',
        templateID: templateInfo?.templateNo,
        isBranded: 'Yes',
        companyID: jobInfo?.companyData?.companyId,
        centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
        companyName: jobInfo?.companyData?.companyName,
        section: 'KeyHighlights',
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      setCompanyHighlightsTracked(true);
    }
  }, [isInView, companyHighlightsTracked]);
  const isSmall = useIsCustomWidth('sm');

  const showHighLights = (show?: boolean) => {
    const vis = isSmall ? 2 : 3;

    const displayedBenefits =
      showAll || show ? highLights : highLights?.slice(0, vis);

    return (
      <Box p={2} pb={0.5} px={1.5} ref={ref}>
        <Card
          variant="outlined"
          sx={{
            border: 'none',
            backgroundColor: open ? '#fff' : '#F9FAFB',
          }}
        >
          <CardContent
            sx={{
              padding: 0,
              '&:last-child': {
                pb: 0,
              },
            }}
          >
            {displayedBenefits.map((b, idx) => (
              <Box
                key={idx}
                display={'flex'}
                alignItems={'center'}
                mb={{xxs: 2, md: 2}}
              >
                <Box
                  borderRadius={'50%'}
                  height={32}
                  width={32}
                  border={
                    '0.6px solid var(--Outlines-Dividers, rgba(126, 126, 126, 0.20))'
                  }
                >
                  <Image
                    src={b?.keyHighlightsIcon || ''}
                    height={30}
                    width={30}
                    style={{borderRadius: '50%'}}
                    alt="icon"
                  />
                </Box>
                <Box ml={2} mt={isAppIimjobs ? 0 : -0.25}>
                  {(b?.keyHighlightsSubheading?.length || 0) > 0 && (
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      lineHeight={'16px'}
                    >
                      {b.keyHighlightsSubheading}
                    </Typography>
                  )}
                  {(b?.keyHighlightsDescription?.length || 0) > 0 && (
                    <Typography
                      fontSize={12}
                      fontWeight={{xxs: 400, md: 500}}
                      mt={'4px'}
                      color={isAppIimjobs ? '#7E7E7E' : '#000'}
                      lineHeight={'16px'}
                    >
                      {b.keyHighlightsDescription}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
            {highLights.length > vis && !show && (
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => {
                  TrackEventUBA('jdPageClick', 'click', {
                    ref: jdQueryParams?.ref || '',
                    pageName: 'Job Description Page',
                    jobID: jobInfo?.id,
                    jobVersion: '',
                    jobRef: getKeyFromPath('ref') || '',
                    jobPos: jdQueryParams?.jobPos || '',
                    page: 'JD',
                    section: 'KeyHighlights',
                    isBranded: 'Yes',
                    sessionUsername: sessionStorage.getItem('userId') || '',
                    typeClick: `KeyHighlights-${
                      showAll ? 'ShowLess' : 'ShowMore'
                    }`,
                    companyID: jobInfo?.companyData?.companyId,
                    centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
                    companyName: jobInfo?.companyData?.companyName,
                    templateID: templateInfo?.templateNo,
                  });
                  if (isSmall) {
                    setOpen(true);
                  } else {
                    toggleShowAll();
                  }
                }}
                sx={{cursor: 'pointer', pb: 1.5}}
              >
                <Link
                  color="primary"
                  underline="none"
                  letterSpacing="0.48px"
                  lineHeight="18px"
                  fontSize={isAppIimjobs ? 12 : 14}
                  fontWeight={isAppIimjobs ? 500 : 600}
                >
                  {showAll ? 'Show Less' : 'Show More'}
                </Link>
                {!isSmall && (
                  <Image
                    src={!showAll ? ChevronDownDark : ChevronUpDark}
                    alt="chevron"
                    height={18}
                    width={18}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <>
      {' '}
      <BottomSheet
        maxHeight={600}
        open={open}
        title={heading}
        onDismiss={() => {
          setOpen(false);
          TrackEventUBA('jdPageClick', 'click', {
            ref: jdQueryParams?.ref || '',
            pageName: 'Job Description Page',
            jobID: jobInfo?.id,
            jobVersion: '',
            jobRef: getKeyFromPath('ref') || '',
            jobPos: jdQueryParams?.jobPos || '',
            page: 'JD',
            section: 'KeyHighlights',
            isBranded: 'Yes',
            sessionUsername: sessionStorage.getItem('userId') || '',
            typeClick: 'KeyHighlights-ShowLess',
            companyID: jobInfo?.companyData?.companyId,
            centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
            companyName: jobInfo?.companyData?.companyName,
            templateID: templateInfo?.templateNo,
          });
        }}
      >
        {showHighLights(true)}
      </BottomSheet>
      {showHighLights()}
    </>
  );
};

export default CompanyHighLights;
